import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

export type JobType = 'provider_validation';
export type JobProgressPhase =
  | 'queued'
  | 'prefetching'
  | 'validating'
  | 'retrying'
  | 'exporting'
  | 'finalizing';
export type JobProgressUnit =
  | 'source_checks'
  | 'providers'
  | 'retry_providers'
  | 'artifacts'
  | 'milestones';
export type JobStatus =
  | 'pending'
  | 'running'
  | 'cancelling'
  | 'done'
  | 'failed'
  | 'cancelled';

export interface JobSourceHealth {
  source: string;
  status: 'healthy' | 'degraded' | 'stale' | 'unavailable';
  reason: string;
  sourceDate: string | null;
  ageHours: number | null;
  checkedAt: string | null;
}

export interface JobProgressDetail {
  phase: JobProgressPhase;
  phaseCurrent: number;
  phaseTotal: number | null;
  unit: JobProgressUnit;
  completedPhases: JobProgressPhase[];
  heartbeatAt: string | null;
  progressUpdatedAt: string | null;
  health: 'normal' | 'degraded';
  sourceHealth?: JobSourceHealth[];
}

export interface JobResponse {
  jobId: string;
  projectId: string;
  jobType: JobType;
  status: JobStatus;
  progress: string | null;
  schemaVersion: number;
  revision: number;
  progressDetail: JobProgressDetail | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  ecsTaskArn: string | null;
}

export interface CancellationResponse {
  cancellationId: string;
  jobId: string;
  status: 'cancelling' | 'cancelled' | 'failed';
  requestedAt: string;
  cancelledAt: string | null;
  reason: string | null;
  error: string | null;
}

@Injectable({ providedIn: 'root' })
export class JobsApiService {
  private readonly http = inject(HttpClient);
  private readonly base = '/api/jobs';

  /**
   * Get job status and progress.
   */
  getJob(jobId: string): Observable<JobResponse> {
    return this.http.get<JobResponse>(`${this.base}/${encodeURIComponent(jobId)}`);
  }

  requestCancellation(jobId: string, reason?: string): Observable<CancellationResponse> {
    return this.http.post<CancellationResponse>(
      `${this.base}/${encodeURIComponent(jobId)}/cancellations`,
      { reason: reason?.trim() || null },
    );
  }
}
